import { spError } from "@eventr/common";
import { ColoursObj } from "@eventr/common-storybook";
import { create } from "zustand";

export const SKIN_FADE_IN_MS = 180;
export const SKIN_FADE_OUT_MS = 220;
export const SKIN_FADE_SWAP_CAP_MS = 500;

/** Applied-skin identity: a scheme, plus the theme id when the scheme is a custom skin. */
export const themeSignature = (scheme: string, customId: string) =>
  `${scheme}|${scheme === "custom" ? customId : ""}`;

/** How a resolved skin reaches the screen: bare, or under the veil. */
export const chooseApplyMode = (firstRun: boolean, currentSig: string, targetSig: string) =>
  !firstRun && currentSig !== targetSig ? "fade" : "bare";

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const nextFrames = (count: number) =>
  new Promise<void>((resolve) => {
    let remaining = count;
    const step = () => {
      remaining -= 1;
      if (remaining <= 0) resolve();
      else requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  });

const swapSafely = (swap: () => void) => {
  try {
    swap();
  } catch (error) {
    spError("Skin fade swap failed: " + String(error));
  }
};

type TSkinFadeState = {
  active: boolean;
  colour: string;
  run: (swap: () => void) => Promise<void>;
};

// Newest run wins; a superseded run abandons its swap.
let generation = 0;

export const useSkinFadeStore = create<TSkinFadeState>()((set) => ({
  active: false,
  colour: "",

  run: async (swap) => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      generation++;
      swapSafely(swap);
      set({ active: false });
      return;
    }
    const gen = ++generation;
    try {
      set({ colour: ColoursObj.backgrounds.primary, active: true });
      await sleep(SKIN_FADE_IN_MS);
      if (gen !== generation) return;
      swapSafely(swap);
      // Backgrounded webviews stop firing rAF; the timeout still lowers the veil.
      await Promise.race([nextFrames(2), sleep(SKIN_FADE_SWAP_CAP_MS)]);
    } finally {
      if (gen === generation) set({ active: false });
    }
  },
}));
